import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { api } from "../api";
import type { Pagination, Scan } from "../types";
import { PageHeader } from "../components/PageHeader";
import { StatusChip } from "../components/StatusChip";
export function ScansPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<Scan[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    const id = setTimeout(() => {
      setLoading(true);
      api<{ scans: Scan[]; pagination: Pagination }>(
        `/admin/scans?status=${status}&search=${encodeURIComponent(search)}`,
      )
        .then((r) => {
          setRows(r.scans);
          setPagination(r.pagination);
          setError("");
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(id);
  }, [status, search]);
  return (
    <>
      <PageHeader
        title="Scans"
        subtitle="Inspect documents, OCR attempts, results and delivery state."
      />
      <Card>
        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} p={2}>
          <TextField
            size="small"
            label="Search scan or client"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            {["QUEUED", "PROCESSING", "COMPLETED", "FAILED"].map((s) => (
              <MenuItem value={s} key={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? (
          <Box p={6} textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Scan</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((s) => (
                <TableRow
                  hover
                  key={s.id}
                  onClick={() => nav(`/scans/${s.id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography className="mono" variant="body2">
                      {s.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{s.client_name ?? s.client_id}</TableCell>
                  <TableCell>
                    <StatusChip value={s.status} />
                  </TableCell>
                  <TableCell>
                    {s.processed_count}/{s.image_count} · {s.failed_count}{" "}
                    failed
                  </TableCell>
                  <TableCell>{date(s.created_at)}</TableCell>
                  <TableCell>{date(s.completed_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Box p={2}>
          <Typography variant="caption" color="text.secondary">
            {pagination?.total ?? 0} scans
          </Typography>
        </Box>
      </Card>
    </>
  );
}
const date = (v: string | null) => (v ? new Date(v).toLocaleString() : "—");
